import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Carousel } from 'antd';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import categoryData from '../../utils/data/category.json';
import CategoryIcon from './CategoryIcon';

// Styled components
const CategoryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '160px',
  height: 'auto',
  minHeight: '57px',
  margin: '0',
  cursor: 'pointer',
  width: '100%',
  padding: '8px 4px',
  [theme.breakpoints.down('md')]: {
    padding: '6px 2px',
    minHeight: '50px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '4px 1px',
    minHeight: '45px',
    maxWidth: '160px',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '4px',
  width: '100%',
  maxWidth: '147px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '120px',
    marginBottom: '3px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '160px',
    marginBottom: '2px',
  },
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '19px',
  textAlign: 'center',
  lineHeight: 1.2,
  fontWeight: 400,
  color: '#333',
  overflow: 'hidden',
  width: '100%',
  maxWidth: '147px',
  whiteSpace: 'normal',
  [theme.breakpoints.down('md')]: {
    fontSize: '16px',
    maxWidth: '120px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    maxWidth: '140px',
    lineHeight: 1.25,
  },
}));

const CustomCarousel = styled(Carousel)(({ theme }) => ({
  // Remove any default pseudo-elements
  '&::before, &::after': {
    display: 'none !important',
  },
  '& *::before, & *::after': {
    display: 'none !important',
  },
  // Carousel container styles
  '& .slick-list': {
    overflow: 'visible',
  },
  '& .slick-track': {
    display: 'flex',
    alignItems: 'flex-start',
  },
  // Dots styling
  '.slick-dots': {
    bottom: '-25px',
    '& li': {
      margin: '0 5px', // Reduce margin between dots (default is 0 5px)
      width: 'auto !important', // Override default width
    },
  },
  '.slick-dots li button': {
    backgroundColor: 'rgba(211, 211, 211, 1)',
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    '&::before': {
      display: 'none !important',
    },
  },
  '.slick-dots li.slick-active button': {
    backgroundColor: 'rgba(244, 118, 42, 1)',
    width: '10px',
    height: '10px',
  },
  // Slide styling
  '.slick-slide': {
    '& > div > div': {
      display: 'flex !important',
      justifyContent: 'center',
    },
    '&::before, &::after': {
      display: 'none !important',
    },
  },
  // Responsive styles
  [theme.breakpoints.down('md')]: {
    '.slick-slide': {
      width: '25% !important', // 4 columns = 25% each
    },
    '.slick-dots': {
      bottom: '-15px',
    },
  }
}));

// Icons mapping using CategoryIcon component
const getCategoryIcon = (categoryID: number, isMobile = false, isTablet = false) => {
  const iconSize = isMobile ? 30 : isTablet ? 30 : 30;
  
  return <CategoryIcon categoryID={categoryID} size={iconSize} />;
};

interface CategoryData {
  category_ID: number;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
  category_Detail: string;
  category_Images: string;
  category_Sort: number;
  category_Status: number;
  paren_id: number;
  category_Date: string;
  type: string;
  category_project: number;
}

interface CategoriesCarouselProps {
  isTablet?: boolean;
  isMobile?: boolean;
}

const CategoriesCarousel: React.FC<CategoriesCarouselProps> = ({ isTablet = false, isMobile = false }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = router;
  const [categories, setCategories] = useState<CategoryData[]>([]);

  useEffect(() => {
    if (categoryData && categoryData.data) {
      const sortedCategories = [...categoryData.data].sort((a, b) => a.category_Sort - b.category_Sort);
      setCategories(sortedCategories);
    }
  }, []);

  // Ant Design Carousel settings
  const getCarouselSettings = () => {
    return {
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: isMobile || isTablet ? 4 : 10, // 4 columns on mobile/tablet, 10 on desktop
      slidesToScroll: isMobile || isTablet ? 4 : 10, // Scroll by 4 items on mobile/tablet (will show 8 with 2 rows)
      rows: isMobile || isTablet ? 2 : 1, // 2 rows on mobile/tablet, 1 row on desktop
      slidesPerRow: 1,
      initialSlide: 0,
      draggable: true,
      swipe: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4, // Scroll by 4 columns (showing 8 items total with 2 rows)
            rows: 2,
            slidesPerRow: 1,
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4, // Scroll by 4 columns (showing 8 items total with 2 rows)
            rows: 2,
            slidesPerRow: 1,
          }
        }
      ]
    };
  };

  const handleCategoryClick = (category: CategoryData) => {
    console.log(`Selected category: ${category.category_ID}`);
    
    let actionText = 'ขาย';
    let categoryName = category.category_Name_TH;
    
    if (locale === 'en') {
      actionText = 'sell';
      categoryName = category.category_Name_EN;
    } else if (locale === 'cn') {
      actionText = '售';
      categoryName = category.category_Name_CN;
    }
    
    const url = `/${actionText}/${categoryName}?endPrice=1000000000`;
    router.push(url);
  };

  const getCategoryLabel = (category: CategoryData) => {
    if (locale === 'en') {
      return category.category_Name_EN || category.category_Name_TH;
    } else if (locale === 'cn') {
      return category.category_Name_CN || category.category_Name_TH;
    }
    return category.category_Name_TH;
  };

  return (
    <Box sx={{ width: '100%', pt: 3, pb: 6, overflowX: 'hidden', maxWidth: '1496px', margin: '0 auto' }}>
      <CustomCarousel {...getCarouselSettings()}>
        {categories.map((category) => (
          <Box key={category.category_ID} sx={{ px: 1,display: 'flex', justifyContent: 'center' }}>
            <CategoryItem onClick={() => handleCategoryClick(category)}>
              <IconContainer>
                {getCategoryIcon(category.category_ID, isMobile, isTablet)}
              </IconContainer>
              <CategoryLabel variant="body2">
                {getCategoryLabel(category)}
              </CategoryLabel>
            </CategoryItem>
          </Box>
        ))}
      </CustomCarousel>
    </Box>
  );
};

export default CategoriesCarousel;